import type { RecordKind } from './record-ref';

/**
 * A thing the rep has @-attached to an assistant turn. Records attach as a
 * reference the assistant resolves via existing tools; a meeting attaches as a
 * handle the get_meeting_* tools resolve. The attachment never carries heavy
 * payloads — it carries ids + a label; the agent pulls detail on demand.
 */
export type AssistantAttachment =
  | { kind: 'contact' | 'account'; id: string; label: string }
  | { kind: 'meeting'; id: string; label: string }
  // Same meeting id, but directs the agent to the transcript rather than the
  // analysis — a distinct attachable so the rep can pull in the raw call.
  | { kind: 'transcript'; id: string; label: string }
  // The rep's territory spotlight in scope. No single contact/account id, so the
  // context is descriptive (the agent reasons over the rep's book of business).
  | { kind: 'spotlight'; id: string; label: string };

/**
 * Origin a conversation was born from (provenance for the index + find-or-open).
 * Spans ALL record kinds — including `spotlight`, deliberately: a spotlight
 * conversation has no per-turn `AssistantAttachment` (the spotlight has no
 * single contact/account id to reference), but it is still *born from* a
 * spotlight and must be findable by that origin. So `ConversationOrigin` is
 * intentionally wider than `AssistantAttachment` here — do not "narrow" it to
 * drop spotlight.
 */
export type ConversationOrigin =
  | { kind: 'meeting'; id: string }
  | { kind: RecordKind; id: string };

/**
 * Build the single developer-role context message seeded on the turn that
 * carries attachments. Foundry stores it server-side so every later turn keeps
 * the context. Returns null when there are no attachments (caller sends plain).
 */
export function buildAttachmentContext(attachments: AssistantAttachment[]): string | null {
  if (!attachments?.length) return null;
  const lines: string[] = ['The user attached the following for this conversation. Use the relevant tools to fetch details on demand — do not ask the user for ids.'];
  for (const a of attachments) {
    switch (a.kind) {
      case 'contact':
        lines.push(`- Contact «${a.label}» (contact_id=${a.id}). Use the contact / rx / activity tools.`);
        break;
      case 'account':
        lines.push(`- Account «${a.label}» (account_id=${a.id}). Use the account / call-plan tools.`);
        break;
      case 'meeting':
        lines.push(`- Meeting «${a.label}» (meeting_id=${a.id}). Use get_meeting_analysis for the summary.`);
        break;
      case 'transcript':
        lines.push(`- Meeting transcript «${a.label}» (meeting_id=${a.id}). Use get_meeting_transcript for the full call.`);
        break;
      case 'spotlight':
        lines.push(`- Territory spotlight «${a.label}». Scope answers to the rep's own book of business — use the rx / call-plan / data-summary tools across their contacts and accounts.`);
        break;
      default: {
        // Exhaustiveness guard: adding a new AssistantAttachment kind without a
        // case here becomes a compile error rather than a silently missing line.
        const _exhaustive: never = a;
        void _exhaustive;
      }
    }
  }
  return lines.join('\n');
}
